/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Params, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { uuid } from '@src/app/model/abstract-object';
import {
    DailyTimeEntry, ModelActivity, RequestAnalysisByUser, RequestAnalysisByUserService
} from '@src/app/modules/crm/request-analysis-by-user/request-analysis-by-user.service';
import { periodName, weekMonday } from '@src/app/modules/crm/request-analysis/analysis-period';
import { ActionPageNavRoute } from '@src/app/routes/action-page-nav';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { FormDurationComponent } from '@src/app/shared/form/form-duration/form-duration.component';

export interface DailyTimeDialogData {
    row: RequestAnalysisByUser;
    userName: string;
    actionsListParams: Params;
}

interface OmcDailyTime {
    omcName: string;
    minutesByWeekday: number[];
    total: number;
}

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

@Component({
    selector: 'app-daily-time-dialog',
    templateUrl: './daily-time-dialog.component.html',
    styleUrl: './daily-time-dialog.component.scss',
    imports: [DialogBannerComponent, MatButtonModule, MatTooltipModule]
})
export class DailyTimeDialogComponent {
    dialogRef = inject<MatDialogRef<DailyTimeDialogComponent>>(MatDialogRef);
    private data = inject<DailyTimeDialogData>(MAT_DIALOG_DATA);
    private dataSvc = inject(RequestAnalysisByUserService);
    private router = inject(Router);

    private durationField = FormDurationComponent.make('Time', 'minutes');

    title = '';
    /** Monday to Sunday, matching the weekday (0 = Monday) on each entry */
    days: Date[] = [];
    omcs: OmcDailyTime[] = [];
    dayTotals: number[] = [];
    weekTotal = 0;
    activity: ModelActivity[] = [];
    ready = false;

    constructor() {
        const row = this.data.row;
        this.title = [this.data.userName, periodName(row.period)].filter(part => part).join(' - ');
        this.days = this.weekDays(row.period);

        const omc = row.omcTeamId ? { omcTeamId: row.omcTeamId } : {};
        const userPeriod = { periodType: row.periodType, period: row.period, userId: row.userId, ...omc };
        forkJoin([this.dataSvc.getDailyTime(userPeriod), this.dataSvc.getActivity(userPeriod)])
            .subscribe(([entries, activity]) => this.show(entries, activity));
    }

    private weekDays(week: string): Date[] {
        const monday = weekMonday(+week.substring(0, 4), +week.substring(5, 7));
        return [0, 1, 2, 3, 4, 5, 6].map(weekday =>
            new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + weekday));
    }

    private show(entries: DailyTimeEntry[], activity: ModelActivity[]) {
        this.omcs = this.groupByOmc(entries);
        this.dayTotals = this.days.map((_, weekday) => sum(this.omcs.map(omc => omc.minutesByWeekday[weekday])));
        this.weekTotal = sum(this.dayTotals);
        this.activity = this.sortParentsBeforeLineItems(this.withMissingParents(activity));
        this.ready = true;
    }

    isLineItem(entry: ModelActivity) {
        return entry.model !== entry.object;
    }

    /** Line items edited without their parent still need a parent row to sit beneath */
    private withMissingParents(activity: ModelActivity[]): ModelActivity[] {
        const parents = new Set(activity.filter(entry => !this.isLineItem(entry)).map(entry => entry.object));
        const orphanedObjects = new Set(activity.filter(entry => !parents.has(entry.object)).map(entry => entry.object));
        const missingParents = Array.from(orphanedObjects).map(object =>
            ({ object, model: object, created: 0, updated: 0, deleted: 0 }));
        return [...activity, ...missingParents];
    }

    private sortParentsBeforeLineItems(activity: ModelActivity[]): ModelActivity[] {
        return activity.sort((a, b) =>
            a.object.localeCompare(b.object)
            || Number(this.isLineItem(a)) - Number(this.isLineItem(b))
            || a.model.localeCompare(b.model));
    }

    /** Audit model names are class names, e.g. ServiceRequest becomes Service Request */
    modelHeading(model: string) {
        return model.replace(/([a-z])([A-Z])/g, '$1 $2');
    }

    formatCount(count: number) {
        return count || '-';
    }

    private groupByOmc(entries: DailyTimeEntry[]): OmcDailyTime[] {
        const omcs = new Map<uuid, OmcDailyTime>();
        for (const entry of entries) {
            if (!omcs.has(entry.omcTeamId)) {
                omcs.set(entry.omcTeamId, { omcName: entry.omcName, minutesByWeekday: [0, 0, 0, 0, 0, 0, 0], total: 0 });
            }
            const omc = omcs.get(entry.omcTeamId);
            omc.minutesByWeekday[entry.weekday] += entry.minutes;
            omc.total += entry.minutes;
        }
        return Array.from(omcs.values());
    }

    dayHeading(day: Date) {
        return day.toLocaleDateString('default', { weekday: 'short' });
    }

    dayToolTip(day: Date) {
        return day.toLocaleDateString('default', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }

    formatMinutes(minutes: number) {
        return minutes ? this.durationField.formatValue(minutes) : '-';
    }

    viewActions() {
        this.dialogRef.close();
        this.router.navigate([ActionPageNavRoute.getListRouteName(), this.data.actionsListParams]);
    }
}
